import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Article } from '@/utils/articles';
import { timeAgo } from '@/utils/format';

export default function HeroCarousel({ items }: { items: Article[] }) {
  const [index, setIndex] = useState(0);
  const touchStart = useRef<number | null>(null);
  const count = items.length;

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => {
      setIndex((v) => (v + 1) % count);
    }, 5200);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  const go = (i: number) => setIndex(((i % count) + count) % count);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(diff) > 50) {
      go(diff > 0 ? index - 1 : index + 1);
    }
    touchStart.current = null;
  };

  return (
    <section className="w-full">
      <div
        className="relative w-full h-[440px] md:h-[540px] rounded-xl overflow-hidden bg-foreground-950"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {items.map((item, i) => (
          <Link
            key={item.id}
            to={`/detail/${item.id}`}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
            }`}
          >
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title}-91黑料娱乐爆料`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/25"></div>
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-10">
              <div className="max-w-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-500 text-background-50">
                    {item.categoryName}
                  </span>
                  <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-background-50/15 text-background-50 backdrop-blur-sm flex items-center gap-1">
                    <i className="ri-fire-line text-accent-400"></i> 焦点爆料
                  </span>
                </div>
                <h2 className="text-2xl md:text-4xl font-black text-background-50 leading-tight line-clamp-2">
                  {item.title}
                </h2>
                <p className="hidden sm:block text-sm md:text-base text-background-200 mt-3 line-clamp-2">
                  {item.excerpt}
                </p>
                <div className="flex items-center gap-4 mt-4 text-xs md:text-sm text-background-300">
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {timeAgo(item.publishTime)}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {item.views.toLocaleString()} 阅读
                  </span>
                </div>
              </div>
            </div>
          </Link>
        ))}

        <button
          type="button"
          aria-label="上一张"
          onClick={(e) => {
            e.preventDefault();
            go(index - 1);
          }}
          className="hidden md:flex absolute left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur-sm hover:bg-background-50/30 transition-colors"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={(e) => {
            e.preventDefault();
            go(index + 1);
          }}
          className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur-sm hover:bg-background-50/30 transition-colors"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-4 right-6 z-20 hidden md:flex items-center gap-2">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`第${i + 1}张`}
              onClick={(e) => {
                e.preventDefault();
                go(i);
              }}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-8 bg-primary-500' : 'w-3 bg-background-50/50'
              }`}
            />
          ))}
        </div>

        <div className="absolute bottom-4 left-6 z-20 flex md:hidden items-center gap-1.5">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`第${i + 1}张`}
              onClick={(e) => {
                e.preventDefault();
                go(i);
              }}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-6 bg-primary-500' : 'w-2 bg-background-50/50'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}